import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setApiBaseUrl, getApiBaseUrl } from "../config/config";
import { sec } from "../utils/security";

export interface ApiConfigurationState {
  isLoading: boolean;
  isConfigured: boolean;
  error: string | null;
  apiBaseUrl: string;
}

export const useApiConfiguration = () => {
  const { isAuthenticated, user } = useAuth0();
  const [state, setState] = useState<ApiConfigurationState>({
    isLoading: false,
    isConfigured: false,
    error: null,
    apiBaseUrl: "",
  });

  const initializeApiConfiguration = async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // Get API base URL from ID token custom claims
      const tokenApiBaseUrl = sec.getApiBaseUrlFromToken();

      if (tokenApiBaseUrl) {
        setApiBaseUrl(tokenApiBaseUrl);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isConfigured: true,
          apiBaseUrl: tokenApiBaseUrl,
        }));
      } else {
        throw new Error("No API base URL found in ID token.");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to configure API base URL";
      console.error("API configuration error:", errorMessage);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        isConfigured: false,
        error: errorMessage,
      }));
    }
  };

  useEffect(() => {
    // Only initialize if user is authenticated and we have user data
    if (isAuthenticated && user) {
      // Add a small delay to ensure user object is fully populated
      const timeoutId = setTimeout(() => {
        initializeApiConfiguration();
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [isAuthenticated, user]);

  return {
    ...state,
    refreshConfiguration: initializeApiConfiguration,
    getCurrentApiUrl: () => getApiBaseUrl(),
  };
};
